import React, { useEffect } from 'react';
import { X, Calendar, Video, ExternalLink, Sparkles } from 'lucide-react';
import { GOOGLE_CALENDAR_DIRECT_URL, GOOGLE_CALENDAR_EMBED_URL } from '../utils/bookingConstants';

const BookingModal = ({ isOpen, onClose, title = 'Schedule a Consultation' }) => {
  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Handle ESC key press
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6 bg-black/80 backdrop-blur-md animate-fadeIn"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="relative w-full max-w-4xl max-h-[92vh] bg-slate-900/95 border border-white/20 rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 bg-slate-950/60">
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-xl bg-blue-500/20 text-blue-400 border border-blue-500/30">
              <Calendar size={20} />
            </div>
            <div>
              <h3 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
                {title}
                <span className="hidden sm:inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                  <Video size={12} /> Google Meet
                </span>
              </h3>
              <p className="text-xs sm:text-sm text-slate-400">
                Choose a suitable date & time for your session
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <a
              href={GOOGLE_CALENDAR_DIRECT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 text-slate-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
              title="Open in new tab"
            >
              <ExternalLink size={18} />
            </a>
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
              aria-label="Close modal"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Mobile Google Meet Badge */}
        <div className="sm:hidden px-4 py-2 bg-emerald-950/40 border-b border-emerald-500/20 flex items-center justify-center gap-2 text-xs text-emerald-300 font-medium">
          <Video size={14} /> Video call with Google Meet included
        </div>

        {/* Modal Body - Embedded Google Calendar */}
        <div className="flex-1 w-full bg-slate-950 relative overflow-y-auto min-h-[500px]">
          <iframe
            src={GOOGLE_CALENDAR_EMBED_URL}
            title="Google Calendar Appointment Scheduling"
            width="100%"
            height="620"
            frameBorder="0"
            className="w-full h-full min-h-[580px] bg-white rounded-b-none"
            allow="camera; microphone; fullscreen; display-capture"
          />
        </div>

        {/* Modal Footer */}
        <div className="px-5 py-3 border-t border-white/10 bg-slate-950/80 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-slate-400">
          <div className="flex items-center gap-1.5 text-slate-300">
            <Sparkles size={14} className="text-blue-400" />
            <span>Automatic Google Meet video invite will be sent to your email.</span>
          </div>
          <a
            href={GOOGLE_CALENDAR_DIRECT_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-400 hover:text-blue-300 hover:underline flex items-center gap-1"
          >
            Having trouble? Open directly in Google Calendar <ExternalLink size={12} />
          </a>
        </div>
      </div>
    </div>
  );
};

export default BookingModal;
